/*
  Component: AgentReferrals
  Description: This component renders a table displaying the direct referrals of an agent.
  Author: Binish Mookken
  Date: April-June 2024

  Description:
  This component renders a table displaying the direct referrals of an agent. It receives an agentInstance prop, which contains information about the agent and their direct referrals. The component utilizes Ant Design's Table component for rendering the table structure.

  Props:
  - agentInstance: An object containing information about the agent and their direct referrals.

  Dependencies:
  - antd: Provides the Table and Tag components for rendering the table and tags.
  - types: Contains type definitions used in the application.
*/

import React from "react";
import { Table } from "antd";
import { AgentInstance, Referral } from "./types";
import "./AgentReferrals.css";

interface AgentReferralsProps {
  agentInstance: AgentInstance | null;
}

const AgentReferrals: React.FC<AgentReferralsProps> = ({ agentInstance }) => {
  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      width: "50%",
      ellipsis: true, // Enable ellipsis for long content
      render: (text: any, record: any) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <div
            className="dot"
            style={{
              backgroundColor: record.status === "active" ? "green" : "red",
            }}
          />

          <div>{record.name}</div>
        </div>
      ),
    },

    {
      title: "Referred Date",
      dataIndex: "referred_date",
      width: "40%",
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Referrals",
      dataIndex: "children_count",
      width: "10%",
    },
  ];

  const dataSource = agentInstance?.agent.referrals || [];

  function countChildrenUpToThreeLevels(person: Referral | undefined): number {
    let count = 0;
    if (!person) return count;
    if (person.children && person.children.length > 0) {
      person.children.forEach((child) => {
        count += 1; // Count the direct child
        if (child.children && child.children.length > 0) {
          child.children.forEach((grandChild) => {
            count += 1; // Count the grandchild
            if (grandChild.children && grandChild.children.length > 0) {
              count += grandChild.children.length; // Count the great-grandchildren
            }
          });
        }
      });
    }
    return count;
  }

  function processReferrals(
    referrals: Referral[] | undefined
  ): { name: string; children_count: number }[] {
    if (!referrals) return [];
    return referrals.map((person) => ({
      name: `${person.first_name} ${person.last_name}`,
      referred_date: person.referred_date,
      status: person.status,
      children_count: countChildrenUpToThreeLevels(person),
    }));
  }

  return (
    <div className="container">
      <div className="title">My Direct Referrals</div>
      <Table
        style={{ borderRadius: "0px" }}
        columns={columns}
        dataSource={processReferrals(agentInstance?.tree[1])}
        pagination={false}
        size="middle"
      />
    </div>
  );
};

export default AgentReferrals;
